<template>
  <!-- 初始化租户弹框（POST /tenant/init） -->
  <el-dialog
    :title="`为${tenantName}初始化租户`"
    :visible.sync="dialogVisible"
    width="640px"
    append-to-body>
    <!-- 模块方案 -->
    <b class="section-title">模块方案</b>
    <el-radio-group v-model="form.modulePlan" size="small">
      <el-radio-button label="all">全量开通</el-radio-button>
      <el-radio-button label="custom">按模块选择</el-radio-button>
    </el-radio-group>
    <div v-if="form.modulePlan === 'custom'" class="module-checks mt-16">
      <el-checkbox-group v-model="form.modules" @change="handleModuleChange">
        <el-checkbox v-for="item in moduleOptions" :key="item.key" :label="item.key">
          {{ item.label }}<el-tooltip v-if="item.desc" effect="dark" :content="item.desc" placement="top">
            <i class="el-icon-question module-desc-icon"></i>
          </el-tooltip>
        </el-checkbox>
      </el-checkbox-group>
    </div>

    <!-- 添加员工 -->
    <div class="display-flex ai-center jc-space-between mt-20 mb-16">
      <b class="section-title margin-0">添加员工</b>
      <el-radio-group v-model="form.staffMode" size="small">
        <el-radio-button label="manual">手动添加</el-radio-button>
        <el-radio-button label="excel">Excel 导入</el-radio-button>
      </el-radio-group>
    </div>
    <!-- 手动添加 -->
    <template v-if="form.staffMode === 'manual'">
      <div class="staff-card" v-for="(staff, index) in form.staffList" :key="index">
        <div class="staff-header">
          <span class="staff-label"><i class="req">*</i>员工姓名 {{ index + 1 }}</span>
          <span class="staff-label"><i class="req">*</i>手机号/工号</span>
          <span class="staff-label"><i class="req">*</i>管理人员</span>
          <span class="staff-label">性别</span>
          <span class="staff-delete" @click="removeStaff(index)">删除</span>
        </div>
        <div class="staff-body">
          <el-input v-model="staff.name" size="small" maxlength="20" placeholder="请输入姓名"></el-input>
          <el-input v-model="staff.account" size="small" maxlength="20" placeholder="请输入手机号或工号"></el-input>
          <el-radio-group v-model="staff.isManager">
            <el-radio :label="1">是</el-radio>
            <el-radio :label="0">否</el-radio>
          </el-radio-group>
          <el-radio-group v-model="staff.gender">
            <el-radio :label="1">男</el-radio>
            <el-radio :label="2">女</el-radio>
          </el-radio-group>
        </div>
      </div>
      <div class="add-staff" @click="addStaff">
        <i class="el-icon-plus mr-6"></i>添加员工
      </div>
    </template>
    <!-- Excel 导入 -->
    <template v-else>
      <div class="display-flex ai-center jc-space-between mb-10">
        <span class="excel-tip"><i class="el-icon-info"></i>请先下载模板，按模板填写后再上传文件。</span>
        <el-button type="text" icon="el-icon-download" @click="downloadTemplate">下载模板</el-button>
      </div>
      <el-upload
        drag
        action=""
        class="excel-upload"
        :auto-upload="false"
        :show-file-list="false"
        accept=".xlsx"
        :on-change="handleFileChange">
        <i class="el-icon-upload"></i>
        <div class="upload-texts">
          <div class="el-upload__text">拖拽或点击选择 Excel 文件</div>
          <div class="el-upload__tip">仅支持 .xlsx 文件，单次上传 1 个，文件不超过 10MB</div>
        </div>
      </el-upload>
      <div v-if="excelFileName" class="excel-result mt-10">
        <i class="el-icon-document color-primary"></i>
        <span class="ml-6">{{ excelFileName }}</span>
        <span class="color-success ml-10">已解析 {{ excelList.length }} 名员工</span>
        <el-button type="text" class="ml-10" @click="clearExcel">重新上传</el-button>
      </div>
    </template>

    <!-- 配置模块默认规则 -->
    <b class="section-title mt-20">配置模块默认规则</b>
    <div class="rule-card">
      <p class="fs-14 fw-bold margin-0 mb-10">隐患排查治理</p>
      <el-checkbox v-model="form.hazardRule" disabled>所有新增员工可登记隐患、任意安全员可确认隐患、任意安全员可验收隐患</el-checkbox>
    </div>

    <span slot="footer">
      <el-button @click="dialogVisible = false">取 消</el-button>
      <el-button type="success" icon="el-icon-magic-stick" :loading="loading" @click="submit">确认初始化</el-button>
    </span>
  </el-dialog>
</template>

<script>
import * as XLSX from 'xlsx'
import { MODULE_OPTIONS, buildMenuIds } from './initModuleIds'

export default {
  name: 'InitTenantDialog', // 初始化租户弹框（一般租户/集团租户共用）
  components: {},
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    tenantId: {
      type: [Number, String],
      default: null
    },
    tenantName: {
      type: String,
      default: ''
    }
  },
  data () {
    return {
      loading: false,
      moduleOptions: MODULE_OPTIONS,
      form: {
        modulePlan: 'all', // all 全量开通 / custom 按模块选择
        modules: [],
        staffMode: 'manual', // manual 手动添加 / excel Excel 导入
        staffList: [],
        hazardRule: true
      },
      excelFileName: '', // 已选择的 Excel 文件名
      excelList: [], // Excel 解析出的员工数据（字段同 TenantInitUserExcelItem）
      prevModules: [] // 上一次的模块勾选值（用于识别本次新勾选项，处理互斥）
    }
  },
  computed: {
    dialogVisible: {
      get () {
        return this.visible
      },
      set (val) {
        this.$emit('update:visible', val)
      }
    }
  },
  watch: {
    visible (val) {
      if (val) this.resetForm()
    }
  },
  methods: {
    /**
     * 打开时重置表单，默认一行员工
     */
    resetForm () {
      this.form = {
        modulePlan: 'all',
        modules: [],
        staffMode: 'manual',
        staffList: [this.newStaff()],
        hazardRule: true
      }
      this.excelFileName = ''
      this.excelList = []
      this.prevModules = []
    },
    /**
     * 按模块选择变化：处理互斥项（如 培训 与 培训（新）只能二选一，勾选其一自动取消另一个）
     */
    handleModuleChange (val) {
      const added = val.filter(k => !this.prevModules.includes(k))
      if (added.length) {
        let modules = val.slice()
        added.forEach(key => {
          const option = this.moduleOptions.find(o => o.key === key)
          if (option && option.conflict) {
            modules = modules.filter(k => !option.conflict.includes(k))
          }
        })
        this.form.modules = modules
      }
      this.prevModules = this.form.modules.slice()
    },
    newStaff () {
      return {
        name: '',
        account: '',
        isManager: 1,
        gender: 1
      }
    },
    addStaff () {
      this.form.staffList.push(this.newStaff())
    },
    removeStaff (index) {
      this.form.staffList.splice(index, 1)
    },
    /**
     * 下载员工导入模板（public/templates 下静态文件）
     */
    downloadTemplate () {
      const a = document.createElement('a')
      a.href = encodeURI(`${process.env.BASE_URL}templates/员工导入模板.xlsx`)
      a.download = '员工导入模板.xlsx'
      a.click()
    },
    /**
     * 上传 Excel 后前端解析为 JSON（字段同 TenantInitUserExcelItem），随初始化接口一并提交
     */
    handleFileChange (file) {
      const raw = file.raw
      if (!raw) return
      // 仅支持 .xlsx，单次上传 1 个，文件不超过 10MB
      if (!/\.xlsx$/i.test(raw.name)) {
        this.$message.warning('仅支持 .xlsx 文件')
        return
      }
      if (raw.size > 10 * 1024 * 1024) {
        this.$message.warning('文件不能超过 10MB')
        return
      }
      const reader = new FileReader()
      reader.onload = (e) => {
        try {
          const wb = XLSX.read(e.target.result, { type: 'array' })
          const ws = wb.Sheets[wb.SheetNames[0]]
          const rows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' })
          // 定位表头行（同时包含 姓名、手机号 的那一行），跳过说明行
          const headIdx = rows.findIndex(r => {
            const cells = r.map(c => String(c).trim())
            return cells.some(c => c.includes('姓名')) && cells.some(c => c.includes('手机号'))
          })
          if (headIdx === -1) {
            this.$message.warning('未找到表头行，请使用下载的模板填写')
            return
          }
          const header = rows[headIdx].map(c => String(c).trim())
          // 表头可能带红色标记或星号，按包含匹配列位置
          const col = (name) => header.findIndex(c => c.includes(name))
          const list = []
          for (let i = headIdx + 1; i < rows.length; i++) {
            const r = rows[i]
            const name = String(r[col('姓名')] || '').trim()
            const mobile = String(r[col('手机号')] || '').trim()
            // 只跳过空行，其余行（含示范数据）原样转换为 JSON
            if (!name && !mobile) continue
            list.push({
              deptName: String(r[col('部门')] || '').trim(),
              name,
              // 性别：0男 1女（默认0）
              sex: String(r[col('性别')] || '').trim() === '女' ? 1 : 0,
              mobile,
              postName: String(r[col('岗位')] || '').trim(),
              // 角色：安全员为安全管理人员角色，其余为员工角色
              managerFlag: String(r[col('角色')] || '').trim() === '安全员' ? 1 : 0,
              email: String(r[col('邮箱')] || '').trim()
            })
          }
          if (!list.length) {
            this.$message.warning('未解析到有效员工数据')
          }
          // 手机号唯一校验
          const seen = new Set()
          const dup = new Set()
          list.forEach(item => {
            if (seen.has(item.mobile)) dup.add(item.mobile)
            seen.add(item.mobile)
          })
          if (dup.size) {
            this.$message.warning(`手机号重复：${[...dup].join('、')}`)
            return
          }
          this.excelList = list
          this.excelFileName = raw.name
          if (list.length) {
            this.$message.success(`解析成功，共 ${list.length} 名员工`)
          }
        } catch (err) {
          this.$message.error('文件解析失败，请使用下载的模板')
        }
      }
      reader.readAsArrayBuffer(raw)
    },
    /**
     * 清除已解析的 Excel，重新上传
     */
    clearExcel () {
      this.excelFileName = ''
      this.excelList = []
    },
    /**
     * 生成 UUID（隐患排查治理编组号）
     */
    genUuid () {
      return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
        const r = Math.random() * 16 | 0
        const v = c === 'x' ? r : (r & 0x3 | 0x8)
        return v.toString(16)
      })
    },
    /**
     * 初始化租户提交：POST /tenant/init
     */
    submit () {
      if (!this.tenantId) {
        this.$message.warning('缺少租户ID，请刷新后重试')
        return
      }
      if (this.form.modulePlan === 'custom' && !this.form.modules.length) {
        this.$message.warning('请至少选择一个模块')
        return
      }
      // 全量开通取 all 整套；按模块选择取所选模块 ID 的并集（EHS/YG 各自去重）
      const payload = {
        tenantId: this.tenantId,
        ...buildMenuIds(this.form.modulePlan, this.form.modules)
      }
      if (this.form.staffMode === 'manual') {
        // 手动添加：过滤掉完全未填写的行
        const staffList = this.form.staffList.filter(s => s.name.trim() || s.account.trim())
        for (const s of staffList) {
          if (!s.name.trim() || !s.account.trim()) {
            this.$message.warning('请完善员工姓名与手机号/工号')
            return
          }
        }
        if (staffList.length) {
          payload.userList = staffList.map(s => ({
            name: s.name.trim(),
            mobile: s.account.trim(),
            managerFlag: s.isManager,
            // 性别：0男 1女（默认0）
            sex: s.gender === 2 ? 1 : 0
          }))
        }
      } else {
        // Excel 导入：上传文件即可，前端解析为 JSON 字符串提交（无有效数据时传空数组）
        if (!this.excelFileName) {
          this.$message.warning('请先上传 Excel 文件')
          return
        }
        payload.userJson = JSON.stringify(this.excelList)
      }
      // 配置模块默认规则：勾选时前端生成 UUID 作为隐患排查治理编组号
      if (this.form.hazardRule) {
        payload.yhGroupNum = this.genUuid()
      }
      this.loading = true
      this.$api.project.initTenant(payload).then(() => {
        this.loading = false
        this.$message.success('初始化成功')
        this.dialogVisible = false
        this.$emit('success')
      }).catch(() => {
        this.loading = false
      })
    }
  }
}
</script>

<style lang="scss" scoped>
// 弹框固定最大高度，内容超出时弹框内部滚动，不产生页面级滚动条
::v-deep .el-dialog {
  display: flex;
  flex-direction: column;
  max-height: 80vh;
  margin: 10vh auto 0 !important;
}
::v-deep .el-dialog__body {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
}
.section-title {
  display: block;
  font-size: 15px;
  margin-bottom: 12px;
  padding-left: 8px;
  border-left: 3px solid #3377FF;
  line-height: 1.2;
}
.module-checks {
  padding: 14px 20px;
  border: 1px dashed #C0C4CC;
  border-radius: 4px;
  background-color: #FAFBFC;
  // el-checkbox-group 会再包一层 div，flex 要作用在它身上
  ::v-deep .el-checkbox-group {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    justify-content: flex-start;
    gap: 10px;
  }
  ::v-deep .el-checkbox {
    margin-right: 0;
  }
  ::v-deep .el-checkbox__label {
    padding-left: 4px;
  }
  // 模块说明提示图标（有 desc 的模块，hover 展示说明）
  .module-desc-icon {
    color: #909399;
    margin-left: 2px;
    cursor: pointer;
    &:hover {
      color: #3377FF;
    }
  }
}
.excel-tip {
  font-size: 13px;
  color: #909399;
  i {
    color: #E6A23C;
    margin-right: 4px;
  }
}
// 上传区域：占满整行，图标与文字横向排列，压缩默认高度
.excel-upload {
  display: block;
  width: 100%;
  ::v-deep .el-upload {
    width: 100%;
  }
  ::v-deep .el-upload-dragger {
    width: 100%;
    height: auto;
    padding: 14px 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    .el-icon-upload {
      font-size: 32px;
      line-height: 1;
      margin: 0 14px 0 0;
    }
    .el-upload__text {
      font-size: 13px;
      text-align: left;
    }
    .el-upload__tip {
      margin-top: 4px;
      text-align: left;
      line-height: 1.4;
    }
  }
}
.staff-card {
  border: 1px solid #EBEEF5;
  border-radius: 4px;
  padding: 14px 16px;
  margin-bottom: 12px;
  .staff-header,
  .staff-body {
    display: grid;
    // 姓名 / 手机号 / 管理人员 / 性别 / 删除，与输入区严格对齐
    grid-template-columns: 1.1fr 1.5fr 96px 96px 40px;
    column-gap: 12px;
    align-items: center;
  }
  .staff-header {
    margin-bottom: 10px;
    .staff-label {
      font-size: 13px;
      color: #606266;
      white-space: nowrap;
      .req {
        color: #F56C6C;
        font-style: normal;
        margin-right: 2px;
      }
    }
    .staff-delete {
      font-size: 13px;
      color: #F56C6C;
      cursor: pointer;
      text-align: right;
      &:hover {
        text-decoration: underline;
      }
    }
  }
  .staff-body {
    ::v-deep .el-radio-group {
      display: flex;
      align-items: center;
      white-space: nowrap;
    }
    ::v-deep .el-radio {
      margin-right: 12px;
      &:last-child {
        margin-right: 0;
      }
    }
    ::v-deep .el-radio__label {
      padding-left: 4px;
    }
  }
}
.add-staff {
  border: 1px dashed #C0C4CC;
  border-radius: 4px;
  text-align: center;
  padding: 10px 0;
  color: #3377FF;
  font-size: 14px;
  cursor: pointer;
  transition: border-color .2s;
  &:hover {
    border-color: #3377FF;
  }
}
.rule-card {
  border: 1px solid #EBEEF5;
  border-radius: 4px;
  padding: 14px 16px;
}
.excel-result {
  display: flex;
  align-items: center;
  padding: 8px 12px;
  border: 1px solid #EBEEF5;
  border-radius: 4px;
  background-color: #FAFBFC;
  font-size: 13px;
}
</style>
